import { Moon, Sun, MonitorCog } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

type Mode = "system" | "light" | "dark";

const order: Mode[] = ["system", "light", "dark"];
const labels: Record<Mode, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};

const ThemeToggle = ({ className }: { className?: string }) => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const current: Mode = (mounted ? (theme as Mode) : "system") ?? "system";

  const cycle = () => {
    const idx = order.indexOf(current);
    const next = order[(idx + 1) % order.length];
    setTheme(next);
  };

  const Icon = current === "system" ? MonitorCog : current === "dark" ? Moon : Sun;
  const nextLabel = labels[order[(order.indexOf(current) + 1) % order.length]];

  return (
    <button
      onClick={cycle}
      className={cn("group relative h-9 w-9 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground transition-colors", className)}
      aria-label={`Theme: ${labels[current]}. Click to switch to ${nextLabel}.`}
      title={`Theme: ${labels[current]} → ${nextLabel}`}
    >
      <span className="absolute inset-0 rounded-full bg-foreground/0 group-hover:bg-foreground/[0.04] transition-colors" />
      <span className="relative h-4 w-4 inline-flex items-center justify-center">
        <AnimatePresence mode="wait" initial={false}>
          {mounted && (
            <motion.span
              key={current}
              initial={{ opacity: 0, rotate: -45, scale: 0.7 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, rotate: 45, scale: 0.7 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 inline-flex items-center justify-center"
            >
              <Icon className="h-4 w-4" strokeWidth={1.6} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </button>
  );
};

export default ThemeToggle;
